import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Image } from "react-bootstrap";
import { ActiveFilterChips, DataTable, DeleteModal, StatusBadge, resolveStatusBadgeVariant } from "CommonComponent";
import Columns from "./component/Columns/Columns";
import Filter from "./component/Filter/Filter";
import {
  EMPTY_APPRAISAL_FILTERS,
  buildActiveFilterChips,
  buildSearchParamsFromState,
  filtersToApiShape,
  parseSearchParamsToFilters,
  resolveFilterLabels,
  sortFromUrlParams,
} from "./utils/appraisalListQuery";
import {
  APPRAISAL_FILTER_URL_CONFIG,
  appraisalFiltersToPersistent,
  getAppraisalModuleKey,
  hasPersistentAppraisalFilters,
  persistentToAppraisalFilters,
} from "./utils/appraisalFilterPersistence";
import { usePersistentFilters } from "store/filters/hooks/usePersistentFilters";
import { moduleFiltersAreEqual } from "store/filters/filterUrlSync";
import { ApprovalModal } from "../component";
import { fetchAppraisalProgress, getAppraisalProgressById, deleteAppraisalProgress, resetAppraisalProgressFlags, submitAppraisalProgress, fetchAppraisalActivityLogs } from "actions";
import * as types from "../../actions/ActionTypes";
import AccessRights from "utils/AccessRights";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import { getObjectViewedIdFromRoute } from "utils/menuUtils";
import { getAppraisalPermissions, getUserRole, getCurrentUserId, getCurrentEmployeeId } from "utils/appraisalPermissions";
import { getSupervisorNameFromRecord } from "../../utils/myPafHelpers";
import StatusSummaryChips from "./component/StatusSummaryChips";
import {
  countStatusSummary,
  isStatusSummaryKey,
  rowMatchesStatusSummary,
} from "./utils/statusSummaryChips";

/**
 * Normalized workflow status for list rules (aligns with `getAppraisalPermissions` / API).
 * Prefer `status`, then `appraisal_status`, then the same Active/Deleted fallback as the Status column.
 */
function normalizeAppraisalProgressRowStatus(row) {
  const raw =
    row?.status ??
    row?.appraisal_status ??
    (row?.is_deleted === "y" ? "Deleted" : "Active");

  let statusText = "pending";
  if (raw !== undefined && raw !== null && raw !== "") {
    if (typeof raw === "string") {
      statusText = raw;
    } else if (typeof raw === "number") {
      statusText = String(raw);
    } else if (raw?.name) {
      statusText = raw.name;
    } else if (Array.isArray(raw) && raw.length > 0) {
      statusText = raw[0]?.name ?? raw[0] ?? "pending";
    }
  }

  statusText = String(statusText).trim().toLowerCase().replace(/\s+/g, "_");
  if (statusText === "sent_rtm") {
    statusText = "sent_to_rtm";
  }
  if (statusText === "delay") {
    statusText = "delayed";
  }
  return statusText;
}

function resolveStatusLevelPermission(row, actionKey) {
  const statusPermissions = row?.appraisal_status_level_permissions;
  if (!statusPermissions || typeof statusPermissions !== "object") {
    // Keep backward compatibility for payloads that don't include status-level permissions.
    return true;
  }

  const map = {
    view: ["can_view", "canView"],
    edit: ["can_edit", "canEdit"],
    delete: ["can_delete", "canDelete"],
    download: ["can_download", "canDownload"],
  };

  const keys = map[actionKey] || [];
  for (const key of keys) {
    const value = statusPermissions[key];
    if (value === true || value === 1 || value === "1") return true;
    if (value === false || value === 0 || value === "0") return false;
  }

  return true;
}

const QUARTER_BY_MONTH = {
  january: "Q1",
  february: "Q1",
  march: "Q1",
  april: "Q2",
  may: "Q2",
  june: "Q2",
  july: "Q3",
  august: "Q3",
  september: "Q3",
  october: "Q4",
  november: "Q4",
  december: "Q4",
};

function entityIdFromField(value) {
  if (value == null) return null;
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return null;
    if (/^\d+$/.test(t)) return t;
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const id = entityIdFromField(item);
      if (id) return id;
    }
    return null;
  }
  if (typeof value === "object") {
    const id = value.id ?? value.campaign_id ?? value.designation_id ?? value.department_id;
    return id != null ? String(id) : null;
  }
  return null;
}

function quartersDetectedForRow(row) {
  const monthValues =
    Array.isArray(row.months) && row.months.length > 0
      ? row.months
      : row.month
        ? String(row.month)
            .split(",")
            .map((m) => m.trim())
            .filter(Boolean)
        : [];

  const fromMonths = [
    ...new Set(
      monthValues
        .map((m) => QUARTER_BY_MONTH[String(m).trim().toLowerCase()])
        .filter(Boolean)
    ),
  ];

  if (fromMonths.length > 0) return fromMonths;

  const dateValue = row.appraisal_date_time || row.date || row.created_at;
  if (!dateValue) return [];
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return [];
  return [`Q${Math.floor(parsed.getMonth() / 3) + 1}`];
}

function rowMatchesMonthFilter(row, monthFilter) {
  if (!monthFilter) return true;
  const target = String(monthFilter).trim().toLowerCase();
  const monthValues =
    Array.isArray(row.months) && row.months.length > 0
      ? row.months.map((m) => String(m).trim().toLowerCase())
      : row.month
        ? String(row.month)
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        : [];
  if (monthValues.some((m) => m === target)) return true;

  const dateValue = row.appraisal_date_time || row.date || row.created_at;
  if (!dateValue) return false;
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return false;
  const longMonth = parsed.toLocaleString("default", { month: "long" }).toLowerCase();
  return longMonth === target;
}

function normalizeFilterText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");
}

function rowMatchesPafStatusFilter(row, pafStatusFilter, options = {}) {
  if (!pafStatusFilter) return true;

  // Probational appraisals are always completed.
  if (options.isProbational) {
    const key = String(pafStatusFilter).trim().toLowerCase();
    return key === "completed";
  }

  if (isStatusSummaryKey(pafStatusFilter)) {
    return rowMatchesStatusSummary(row, pafStatusFilter);
  }

  const target = normalizeFilterText(pafStatusFilter);
  const rowStatus =
    row?.status ??
    row?.appraisal_status ??
    row?.workflow_status ??
    row?.paf_status ??
    "";
  const normalizedRowStatus =
    typeof rowStatus === "string"
      ? normalizeFilterText(rowStatus)
      : normalizeFilterText(rowStatus?.name || (Array.isArray(rowStatus) ? rowStatus[0]?.name || rowStatus[0] : ""));
  return normalizedRowStatus === target;
}

function rowMatchesSupervisorFilter(row, supervisorFilter) {
  if (!supervisorFilter) return true;
  const target = normalizeFilterText(supervisorFilter);
  const getSupervisorDisplayName = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (Array.isArray(value)) {
      for (const item of value) {
        const candidate = getSupervisorDisplayName(item);
        if (candidate) return candidate;
      }
      return "";
    }
    if (typeof value === "object") {
      const nameParts = [value.firstname, value.middlename, value.lastname]
        .map((part) => (part == null ? "" : String(part).trim()))
        .filter(Boolean);
      if (nameParts.length > 0) return nameParts.join(" ");
      return String(
        value.full_name ||
          value.name ||
          value.employee_name ||
          value.supervisor_name ||
          value.label ||
          ""
      ).trim();
    }
    return "";
  };
  const rowSupervisor =
    getSupervisorDisplayName(row?.supervisor_name) ||
    getSupervisorDisplayName(row?.supervisor) ||
    getSupervisorDisplayName(row?.employee?.supervisor_name) ||
    getSupervisorDisplayName(row?.employee?.supervisor);
  return normalizeFilterText(rowSupervisor) === target;
}

/** Quarterly list: narrow rows in-browser (no API filter params except frequency). */
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function normalizeListStatusKey(status) {
  let statusText = "pending";
  if (status) {
    if (typeof status === "string") {
      statusText = status;
    } else if (typeof status === "number") {
      statusText = String(status);
    } else if (status.name) {
      statusText = status.name;
    } else if (Array.isArray(status) && status.length > 0) {
      statusText = status[0].name || "pending";
    }
  }
  statusText = String(statusText).trim().toLowerCase().replace(/\s+/g, "_");
  if (statusText === "sent_rtm") {
    statusText = "sent_to_rtm";
  }
  if (statusText === "delay") {
    statusText = "delayed";
  }
  return statusText;
}

function applyClientFilters(rows, f, options = {}) {
  if (!rows?.length || !f) return rows;

  return rows.filter((row) => {
    if (f.campaign) {
      const rid =
        entityIdFromField(row.campaign_id) ??
        entityIdFromField(row.employee?.campaign) ??
        entityIdFromField(row.employee?.campaigns) ??
        entityIdFromField(row.campaign) ??
        entityIdFromField(row.campaigns);
      if (!rid || String(rid) !== String(f.campaign)) return false;
    }
    if (f.designation) {
      const rid =
        entityIdFromField(row.designation_id) ??
        entityIdFromField(row.employee?.designation) ??
        entityIdFromField(row.employee?.designations) ??
        entityIdFromField(row.designation);
      if (!rid || String(rid) !== String(f.designation)) return false;
    }
    if (f.department) {
      const rid =
        entityIdFromField(row.department_id) ??
        entityIdFromField(row.employee?.department) ??
        entityIdFromField(row.department);
      if (!rid || String(rid) !== String(f.department)) return false;
    }
    if (f.month && !rowMatchesMonthFilter(row, f.month)) return false;
    if (f.quarter) {
      const qs = quartersDetectedForRow(row);
      if (!qs.includes(f.quarter)) return false;
    }
    if (f.pafStatus && !rowMatchesPafStatusFilter(row, f.pafStatus, options)) return false;
    if (f.supervisor && !rowMatchesSupervisorFilter(row, f.supervisor)) return false;
    return true;
  });
}

const AppraisalProgressList = ({ baseFilters = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const appraisalState = useSelector((state) => state.appraisalState);
  const session = useSelector((state) => state.session);
  const employeeState = useSelector((state) => state.employeeState);
  const routeObjectViewedId = useObjectViewedId();

  const moduleKey = useMemo(
    () => getAppraisalModuleKey(location.pathname, baseFilters),
    [location.pathname, baseFilters]
  );

  const {
    filters: flatFilters,
    setFilters: setPersistentFilters,
    isInitialized: filtersInitialized,
  } = usePersistentFilters(moduleKey, {
    syncUrl: false,
    urlConfig: APPRAISAL_FILTER_URL_CONFIG,
    defaultFilters: {},
  });

  const skipUrlEffectRef = useRef(false);
  const initialUrlHydratedRef = useRef(false);
  const urlHydrationCompleteRef = useRef(false);
  const lastFetchKeyRef = useRef(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState(null);
  const [refreshDataTable, setRefreshDataTable] = useState(false);
  const [tableQuery, setTableQuery] = useState(() => ({
    page: 1,
    pageSize: 10,
    search: searchParams.get("q") || "",
    sort: sortFromUrlParams(searchParams),
  }));
  const [recordIdToEdit, setRecordIdToEdit] = useState(null);
  const [approvalModal, setApprovalModal] = useState({ show: false, appraisalId: null, actionType: null });
  const [listError, setListError] = useState(null);
  const [filterOptionsReady, setFilterOptionsReady] = useState(false);
  const debouncedTableSearch = useDebouncedValue(tableQuery.search, 400);
  const pendingMonthlyViewNavigationRef = useRef(false);

  // Get object_viewed_id for fetching data
  const currentPagePermissions =
    session?.current_page_permissions && !Array.isArray(session.current_page_permissions)
      ? session.current_page_permissions
      : null;
  const isPermissionsForCurrentRoute =
    currentPagePermissions?.page_url === location.pathname;
  const object_viewed_id =
    routeObjectViewedId ||
    session?.current_object_viewed_id ||
    (isPermissionsForCurrentRoute ? currentPagePermissions?.object_id : null) ||
    (session?.nested_menus ? getObjectViewedIdFromRoute(session.nested_menus, location.pathname) : null) ||
    "";

  const isQuarterlyListForClientFilters =
    String(baseFilters?.frequency || "").toLowerCase() === "quarterly" ||
    location.pathname.startsWith("/quarterly-appraisal");

  const listSource = String(baseFilters?.listSource || "").toLowerCase();
  const isEngineList = listSource === "engine";
  const isAggregatedList = listSource === "aggregated";

  const isProbational =
    ["probationary", "probational"].includes(
      String(baseFilters?.frequency || "").toLowerCase()
    ) ||
    location.pathname.startsWith("/probational-appraisals") ||
    location.pathname.startsWith("/probationary-appraisals");

  const filters = useMemo(
    () => persistentToAppraisalFilters(flatFilters),
    [flatFilters]
  );

  const resolvedFilters = useMemo(
    () =>
      resolveFilterLabels(filters, {
        campaigns: employeeState.campaignsList || [],
        designations: employeeState.designationsList || [],
        departments: employeeState.departmentsList || [],
      }),
    [filters, employeeState.campaignsList, employeeState.designationsList, employeeState.departmentsList]
  );

  const apiFilterShape = useMemo(() => filtersToApiShape(resolvedFilters), [resolvedFilters]);

  const syncListUrl = useCallback(
    (nextFilters, nextTableQuery, flatOverride = null) => {
      const flat = flatOverride ?? appraisalFiltersToPersistent(nextFilters);

      if (!moduleFiltersAreEqual(flat, flatFilters)) {
        setPersistentFilters(flat);
      }

      const params = buildSearchParamsFromState({
        filters: nextFilters,
        sort: nextTableQuery?.sort,
        search: nextTableQuery?.search,
      });

      if (params.toString() === searchParams.toString()) {
        return;
      }

      skipUrlEffectRef.current = true;
      setSearchParams(params, { replace: true });
    },
    [flatFilters, searchParams, setPersistentFilters, setSearchParams]
  );

  const getMergedFilters = () => {
    const base = baseFilters || {};
    if (isQuarterlyListForClientFilters) {
      const merged = {
        ...base,
        frequency: base.frequency || "Quarterly",
      };
      if (debouncedTableSearch.trim()) {
        merged.search = debouncedTableSearch.trim();
      }
      return merged;
    }
    const combined = { ...base, ...apiFilterShape };
    // Summary chips filter client-side across related statuses; omit from API.
    if (combined.pafStatus && isStatusSummaryKey(combined.pafStatus)) {
      const { pafStatus, ...rest } = combined;
      return Object.keys(rest).length > 0 ? rest : null;
    }
    return Object.keys(combined).length > 0 ? combined : null;
  };

  /** Quarterly: chip filters stay client-side; debounced search refetches from API with `search`. */
  const refetchFiltersKey = useMemo(() => {
    if (isQuarterlyListForClientFilters) {
      return JSON.stringify({
        __quarterly: true,
        listSource,
        search: debouncedTableSearch.trim(),
      });
    }
    return JSON.stringify({ ...(apiFilterShape ?? {}), listSource });
  }, [isQuarterlyListForClientFilters, apiFilterShape, debouncedTableSearch, listSource]);

  const loadAppraisalList = useCallback(() => {
    if (!object_viewed_id) return Promise.resolve();
    setListError(null);
    return dispatch(fetchAppraisalProgress(object_viewed_id, getMergedFilters()))
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch appraisal records. Please try again.";
        setListError(message);
      })
      .finally(() => setFilterOptionsReady(true));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dispatch, object_viewed_id, apiFilterShape, debouncedTableSearch, baseFilters, isQuarterlyListForClientFilters]
  );

  // Restore persisted filters into the URL before the first list fetch.
  useLayoutEffect(() => {
    if (!filtersInitialized || initialUrlHydratedRef.current) {
      return;
    }

    initialUrlHydratedRef.current = true;

    const urlFilters = parseSearchParamsToFilters(searchParams);
    const urlHasFilters = Object.values(urlFilters).some((entry) => entry?.value);
    const hasPersisted = hasPersistentAppraisalFilters(flatFilters);

    if (!urlHasFilters && hasPersisted) {
      const appraisalFilters = persistentToAppraisalFilters(flatFilters);
      syncListUrl(appraisalFilters, tableQuery, flatFilters);
    }

    urlHydrationCompleteRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInitialized]);

  // Restore sort/search when user navigates with browser back/forward.
  useEffect(() => {
    if (skipUrlEffectRef.current) {
      skipUrlEffectRef.current = false;
      return;
    }

    const nextSort = sortFromUrlParams(searchParams);
    const nextSearch = searchParams.get("q") || "";
    setTableQuery((prev) => {
      const sortUnchanged =
        (prev.sort?.columnId ?? null) === (nextSort?.columnId ?? null) &&
        (prev.sort?.direction ?? null) === (nextSort?.direction ?? null);
      const searchUnchanged = (prev.search ?? "") === nextSearch;

      if (sortUnchanged && searchUnchanged) {
        return prev;
      }

      return {
        ...prev,
        search: nextSearch,
        sort: nextSort,
        page: 1,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Fetch appraisal progress list once filters are hydrated and request inputs are stable.
  useEffect(() => {
    if (!object_viewed_id || !filtersInitialized || !urlHydrationCompleteRef.current) {
      return;
    }

    const fetchKey = `${object_viewed_id}|${location.pathname}|${listSource}|${refetchFiltersKey}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;
    loadAppraisalList();
  }, [
    object_viewed_id,
    filtersInitialized,
    location.pathname,
    refetchFiltersKey,
    loadAppraisalList,
  ]);

  // Refresh data after delete
  useEffect(() => {
    if (refreshDataTable && object_viewed_id) {
      lastFetchKeyRef.current = null;
      loadAppraisalList();
      setRefreshDataTable(false);
    }
  }, [refreshDataTable, object_viewed_id, loadAppraisalList]);

  // Navigate to edit form when update is triggered
  useEffect(() => {
    if (appraisalState.showAppraisalProgressUpdateForm && appraisalState.appraisalProgressRecord && recordIdToEdit) {
      navigate("/appraisalprogressform", {
        state: {
          editMode: true,
          recordId: recordIdToEdit,
          from: location.pathname,
          objectViewedId: object_viewed_id,
        },
      });
      // Reset flags so navigating back to list doesn't immediately redirect again
      dispatch(resetAppraisalProgressFlags());
      setRecordIdToEdit(null); // Clear after navigation
    }
  }, [appraisalState.showAppraisalProgressUpdateForm, appraisalState.appraisalProgressRecord, recordIdToEdit, navigate, dispatch]);

  const isMonthlyReportsRoute = location.pathname.startsWith("/monthly-reports");
  const monthlyAppraisalViewPath = isMonthlyReportsRoute
    ? "/monthlyappraisalreport"
    : "/monthlyappraisalview";
  const isQuarterlyList =
    location.pathname.startsWith("/quarterly-appraisal") ||
    String(baseFilters?.frequency || "").toLowerCase() === "quarterly";
  const isYearlyList =
    location.pathname.startsWith("/yearly-appraisals") ||
    String(baseFilters?.frequency || "").toLowerCase() === "yearly";
  const enginePafViewPath = isQuarterlyList
    ? "/quarterly-appraisal/paf/view"
    : isYearlyList
      ? "/yearly-appraisals/paf/view"
      : monthlyAppraisalViewPath;
  const aggregatedViewPath = isQuarterlyList
    ? "/quarterly-appraisal/view"
    : isYearlyList
      ? "/yearly-appraisals/view"
      : monthlyAppraisalViewPath;

  // Clear stale view-navigation state when returning to the list (Cancel / sidebar).
  useEffect(() => {
    pendingMonthlyViewNavigationRef.current = false;
    dispatch(resetAppraisalProgressFlags());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to view page only after an explicit View action from this list.
  useEffect(() => {
    if (
      pendingMonthlyViewNavigationRef.current &&
      appraisalState.showMonthlyAppraisalViewPage &&
      appraisalState.appraisalProgressRecord
    ) {
      pendingMonthlyViewNavigationRef.current = false;
      navigate(isEngineList ? enginePafViewPath : monthlyAppraisalViewPath, {
        state: {
          recordId: appraisalState.appraisalProgressRecord?.id,
          record: appraisalState.appraisalProgressRecord,
          from: location.pathname,
        },
      });
      dispatch(resetAppraisalProgressFlags());
    }
  }, [
    appraisalState.showMonthlyAppraisalViewPage,
    appraisalState.appraisalProgressRecord,
    navigate,
    dispatch,
    monthlyAppraisalViewPath,
    enginePafViewPath,
    isEngineList,
    location.pathname,
  ]);

  // Get data from Redux state
  const rows = appraisalState.appraisalProgressList || [];

  const displayRows = useMemo(() => {
    if (!apiFilterShape || Object.keys(apiFilterShape).length === 0) return rows;
    return applyClientFilters(rows, apiFilterShape, { isProbational });
  }, [rows, apiFilterShape, isProbational]);

  const statusSummaryCounts = useMemo(() => {
    const filtersWithoutStatus = { ...(apiFilterShape || {}) };
    delete filtersWithoutStatus.pafStatus;
    const rowsForCounts =
      Object.keys(filtersWithoutStatus).length > 0
        ? applyClientFilters(rows, filtersWithoutStatus, { isProbational })
        : rows;
    // Rows from probational_appraisals are always completed (no PAF workflow status).
    if (isProbational) {
      return {
        delayed: 0,
        completed: rowsForCounts.length,
        pending: 0,
      };
    }
    return countStatusSummary(rowsForCounts);
  }, [rows, apiFilterShape, isProbational]);

  const activeChips = useMemo(
    () => buildActiveFilterChips(resolvedFilters, tableQuery.sort),
    [resolvedFilters, tableQuery.sort]
  );

  // Helpers for columns
  const getEmployeeName = (employee) => {
    if (!employee) return "";
    
    // If it's already a string, return it
    if (typeof employee === 'string') return employee;
    
    // If employee has firstname, middlename, lastname fields, construct full name
    if (employee.firstname || employee.lastname) {
      const nameParts = [
        employee.firstname,
        employee.middlename,
        employee.lastname
      ].filter(Boolean); // Remove null/undefined/empty values
      
      if (nameParts.length > 0) {
        return nameParts.join(' ').trim();
      }
    }
    
    // Fallback to other possible field names
    if (employee.name) return employee.name;
    if (employee.client_name) return employee.client_name;
    if (employee.employee_name) return employee.employee_name;
    if (employee.full_name) return employee.full_name;
    
    // Handle array case
    if (Array.isArray(employee) && employee.length > 0) {
      const emp = employee[0];
      if (emp.firstname || emp.lastname) {
        const nameParts = [
          emp.firstname,
          emp.middlename,
          emp.lastname
        ].filter(Boolean);
        if (nameParts.length > 0) {
          return nameParts.join(' ').trim();
        }
      }
      return emp.name || emp.client_name || emp.employee_name || "";
    }
    
    return "";
  };

  const getDepartmentName = (department) => {
    if (!department) return "";
    if (typeof department === 'string') return department;
    if (department.name) return department.name;
    if (department.department_name) return department.department_name;
    if (Array.isArray(department) && department.length > 0) {
      const dept = department[0];
      return dept.name || dept.department_name || "";
    }
    return "";
  };

  const getCampaignName = (campaign) => {
    if (!campaign) return "";
    if (typeof campaign === 'string') return campaign;
    
    // Handle array of campaigns - show first one or join if multiple
    if (Array.isArray(campaign)) {
      if (campaign.length === 0) return "";
      // If array contains objects, get names from each
      const names = campaign
        .map(camp => {
          if (typeof camp === 'string') return camp;
          if (typeof camp === 'object' && camp) {
            return camp.name || camp.client_name || camp.campaign_name || "";
          }
          return "";
        })
        .filter(Boolean);
      return names.length > 0 ? names.join(", ") : "";
    }
    
    // Handle single campaign object
    if (typeof campaign === 'object') {
      return campaign.name || campaign.client_name || campaign.campaign_name || "";
    }
    
    return "";
  };

  const getDesignationName = (designation) => {
    if (!designation) return "";
    if (typeof designation === 'string') return designation;
    
    // Handle array of designations - show first one or join if multiple
    if (Array.isArray(designation)) {
      if (designation.length === 0) return "";
      // If array contains objects, get names from each
      const names = designation
        .map(desig => {
          if (typeof desig === 'string') return desig;
          if (typeof desig === 'object' && desig) {
            return desig.name || desig.designation_name || "";
          }
          return "";
        })
        .filter(Boolean);
      return names.length > 0 ? names.join(", ") : "";
    }
    
    // Handle single designation object
    if (typeof designation === 'object') {
      return designation.name || designation.designation_name || "";
    }
    
    return "";
  };

  const getAppraiserName = (row) => getSupervisorNameFromRecord(row);

  const getAppraisalFormName = (row) => {
    const employee = row?.employee;
    if (employee?.appraisal_search_text) {
      return String(employee.appraisal_search_text).trim();
    }
    const directName = String(
      row?.appraisal_form_name || row?.setup_name || row?.paf_name || ""
    ).trim();
    if (directName) return directName;
    return getAppraisalName(
      employee?.appraisal ||
        row.appraisal_name ||
        row.setup_name ||
        row.appraisal_setup ||
        row.appraisal_setups
    );
  };

  const getAppraisalName = (appraisal) => {
    if (!appraisal) return "";
    if (typeof appraisal === 'string') return appraisal;
    
    // Handle array of appraisals - show first one or join if multiple
    if (Array.isArray(appraisal)) {
      if (appraisal.length === 0) return "";
      // If array contains objects, get titles from each
      const names = appraisal
        .map(app => {
          if (typeof app === 'string') return app;
          if (typeof app === 'object' && app) {
            return app.title || app.name || app.appraisal_title || "";
          }
          return "";
        })
        .filter(Boolean);
      return names.length > 0 ? names.join(", ") : "";
    }
    
    // Handle single appraisal object
    if (typeof appraisal === 'object') {
      return appraisal.title || appraisal.name || appraisal.appraisal_title || "";
    }
    
    return "";
  };

  const formatDate = (date) => {
    if (!date) return "";
    if (typeof date === 'string') {
      try {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString();
      } catch {
        return date;
      }
    }
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return "";
  };

  const getStatusLabel = (status) => {
    const statusText = normalizeListStatusKey(status);
    if (statusText === "delayed" || statusText === "delay") return "Delayed";
    if (statusText === "pending" || statusText === "draft") return "Pending";
    // Probationary / aggregate rows fall back to "Active" (is_active) — treat as completed.
    if (statusText === "active") return "Completed";
    if (
      statusText === "completed" ||
      statusText === "auto_approved" ||
      statusText === "signed_off"
    ) {
      return statusText === "auto_approved"
        ? "Auto Approved"
        : statusText === "signed_off"
          ? "Signed Off"
          : "Completed";
    }
    return statusText.replace(/_/g, " ");
  };

  const getStatus = (status) => {
    const statusText = normalizeListStatusKey(status);
    const label = getStatusLabel(status);
    const badgeStatus =
      statusText === "delay"
        ? "delayed"
        : statusText === "active"
          ? "completed"
          : statusText;

    return (
      <StatusBadge
        label={label}
        variant={resolveStatusBadgeVariant(badgeStatus)}
      />
    );
  };

  const getActions = (row) => {
    const currentPermissions = session?.current_page_permissions;

    // Get workflow permissions
    const userRole = getUserRole(session);
    const currentUserId = getCurrentUserId(session);
    const permissions = getAppraisalPermissions({
      role: userRole,
      record: row,
      currentUserId: currentUserId,
      currentEmployeeId: getCurrentEmployeeId(session),
    });

    const isSupervisorRole =
      String(userRole || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/-/g, "_") === "supervisor";

    // Base rights from page permissions (if available)
    const hasPagePermissions =
      currentPermissions && !Array.isArray(currentPermissions);

    const hasStatusLevelView = resolveStatusLevelPermission(row, "view");
    const hasStatusLevelEdit = resolveStatusLevelPermission(row, "edit");
    const hasStatusLevelDelete = resolveStatusLevelPermission(row, "delete");

    const canView =
      hasPagePermissions &&
      AccessRights(currentPermissions, "view", row.created_by) &&
      hasStatusLevelView;

    const canEditBase =
      hasPagePermissions &&
      AccessRights(currentPermissions, "edit", row.created_by);

    const canDelete =
      hasPagePermissions &&
      AccessRights(currentPermissions, "delete", row.created_by) &&
      hasStatusLevelDelete;

    const oneLevelRow = Number(row.approval_levels ?? row.approvalLevels) === 1;
    const canEdit =
      !isAggregatedList &&
      row.is_deleted !== "y" &&
      canEditBase &&
      hasStatusLevelEdit &&
      (oneLevelRow
        ? permissions.canEditDraft
        : !isSupervisorRole || permissions.canEditDraft);

    const canDownload =
      location.pathname.startsWith("/monthly-reports") &&
      canView &&
      (row?.can_download === true ||
        row?.can_download === 1 ||
        row?.can_download === "1" ||
        resolveStatusLevelPermission(row, "download"));

    /**
     * Supervisors: list Edit only for statuses where they act on the appraisal (pending draft,
     * returned_to_supervisor). Other roles unchanged. Backend `appraisal_status_level_permissions`
     * still gates via hasStatusLevelEdit / canEdit above.
     */
    const rowStatusKey = normalizeAppraisalProgressRowStatus(row);
    const supervisorPendingOnlyListEdit =
      !isSupervisorRole ||
      rowStatusKey === "pending" ||
      rowStatusKey === "returned_to_supervisor";

    return (
      <div
        className="actionClass"
        style={{ textAlign: "center", whiteSpace: "nowrap" }}
      >
        {canView && (
          <Image
            src="/assets/images/view-icon.svg"
            width={20}
            className="me-2 cursor-pointer"
            title="View"
            onClick={() => viewRecord(row)}
          />
        )}

        {canDownload && (
          <Image
            src="/assets/images/download-pdf-icon.svg"
            width={20}
            className="me-2 cursor-pointer"
            title="Download"
            onClick={() => downloadRecord(row)}
          />
        )}

        {!isProbational && !isAggregatedList && canEdit && supervisorPendingOnlyListEdit && (
          <Image
            src="/assets/images/edit-icon.svg"
            width={20}
            className="me-2 cursor-pointer"
            title="Edit"
            onClick={() => updateRecord(row.id)}
          />
        )}

        {/* Workflow Actions */}
        {!isProbational && permissions.canSubmit && (
          <button
            className="btn btn-sm btn-info me-1"
            title="Submit"
            onClick={() => handleSubmit(row.id)}
            style={{ fontSize: '11px', padding: '2px 8px' }}
          >
            Submit
          </button>
        )}

        {/* Approve/Reject actions are intentionally hidden on list view. */}

        {!isProbational && canDelete && 
        row.is_deleted !== "y" && (
          <Image
            src="/assets/images/delete-icon.svg"
            width={20}
            className="cursor-pointer"
            title="Delete"
            onClick={() => {
              setRowIdToDelete(row.id);
              setOpenDeleteModal(true);
            }}
          />
        )}
      </div>
    );
  };

  const handleSubmit = async (id) => {
    if (window.confirm('Are you sure you want to submit this appraisal for review?')) {
      try {
        await dispatch(submitAppraisalProgress(id, object_viewed_id));
        setRefreshDataTable(true);
      } catch (error) {
        console.error('Error submitting appraisal:', error);
      }
    }
  };

  const handleApprovalSuccess = () => {
    setApprovalModal({ show: false, appraisalId: null, actionType: null });
    setRefreshDataTable(true);
  };

  const updateRecord = (id) => {
    setRecordIdToEdit(id);
    dispatch(getAppraisalProgressById(id));
  };

  const downloadRecord = (row) => {
    navigate(monthlyAppraisalViewPath, {
      state: {
        recordId: row.id,
        record: row,
        autoDownload: true,
        from: location.pathname,
      },
    });
  };

  const viewRecord = (row) => {
    if (isProbational) {
      dispatch({ 
        type: types.APPRAISAL_PROGRESS_GET_SUCCESS, 
        record: row, 
        actionType: "quarterly_view" 
      });
      navigate("/probational-appraisals/view", { state: { record: row, from: location.pathname } });
      return;
    }
    if (isAggregatedList) {
      navigate(aggregatedViewPath, { state: { record: row, from: location.pathname } });
      return;
    }
    pendingMonthlyViewNavigationRef.current = true;
    dispatch(getAppraisalProgressById(row.id, "view"));
  };

  const columns = useMemo(
    () =>
      Columns({
        getEmployeeName,
        getAppraisalFormName,
        getAppraiserName,
        getDepartmentName,
        getCampaignName,
        getDesignationName,
        formatDate,
        getStatusLabel,
        getStatus,
        getActions,
      }),
    // Callbacks are recreated each render; depend on route/session instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, session?.current_page_permissions, session?.user, isProbational, isAggregatedList, isEngineList]
  );

  const isQuarterlyByProp = String(baseFilters?.frequency || "").toLowerCase() === "quarterly";
  const isQuarterlyByRoute = location.pathname.startsWith("/quarterly-appraisal");
  const isQuarterly = isQuarterlyByProp || isQuarterlyByRoute;

  const isYearlyByProp = String(baseFilters?.frequency || "").toLowerCase() === "yearly";
  const isYearlyByRoute = location.pathname.startsWith("/yearly-appraisals");
  const isYearly = isYearlyByProp || isYearlyByRoute;
  const columnsForView = useMemo(
    () => (isQuarterly ? columns : columns.filter((col) => col.id !== "quarter")),
    [columns, isQuarterly]
  );

  // Delete handler
  const deleteRecord = async () => {
    if (rowIdToDelete) {
      await dispatch(deleteAppraisalProgress(rowIdToDelete, object_viewed_id));
      setRefreshDataTable(true);
      setOpenDeleteModal(false);
      setRowIdToDelete(null);
    }
  };

  const hideDeleteModal = () => {
    setOpenDeleteModal(false);
    setRowIdToDelete(null);
  };

  const handleFilterChange = (newFilters) => {
    let nextTableQuery;
    setTableQuery((prev) => {
      nextTableQuery = { ...prev, page: 1 };
      return nextTableQuery;
    });
    syncListUrl(newFilters, nextTableQuery);
  };

  const showStatusSummaryChips =
    !isQuarterly && !isYearly && !isMonthlyReportsRoute;

  const activeStatusSummaryKey = isStatusSummaryKey(resolvedFilters?.pafStatus?.value)
    ? resolvedFilters.pafStatus.value
    : null;

  const handleStatusSummarySelect = (chip) => {
    const isSame = resolvedFilters?.pafStatus?.value === chip.key;
    handleFilterChange({
      ...resolvedFilters,
      pafStatus: isSame ? null : { value: chip.key, label: chip.label },
    });
  };

  const handleStatusSummaryClear = () => {
    if (!resolvedFilters?.pafStatus) return;
    handleFilterChange({
      ...resolvedFilters,
      pafStatus: null,
    });
  };

  const handleTableQueryChange = useCallback(
    (updater) => {
      setTableQuery((prev) => {
        const nextTableQuery = typeof updater === "function" ? updater(prev) : updater;

        const searchChanged = (prev.search ?? "") !== (nextTableQuery.search ?? "");
        const sortChanged =
          (prev.sort?.columnId ?? null) !== (nextTableQuery.sort?.columnId ?? null) ||
          (prev.sort?.direction ?? null) !== (nextTableQuery.sort?.direction ?? null);

        // URL tracks filters/sort/search only — pagination is client-side.
        if (searchChanged || sortChanged) {
          syncListUrl(filters, nextTableQuery);
        }

        return nextTableQuery;
      });
    },
    [filters, syncListUrl]
  );

  const handleRemoveChip = (chip) => {
    if (chip.type === "sort") {
      handleTableQueryChange((prev) => ({ ...prev, sort: null, page: 1 }));
      return;
    }
    const nextFilters = {
      ...filters,
      [chip.key]: null,
    };
    handleFilterChange(nextFilters);
  };

  const handleClearAllFilters = () => {
    let nextTableQuery;
    setTableQuery((prev) => {
      nextTableQuery = { ...prev, sort: null, search: "", page: 1 };
      return nextTableQuery;
    });
    syncListUrl(EMPTY_APPRAISAL_FILTERS, nextTableQuery, {});
  };

  return (
    <div className="main-content-div page-card">
      {listError ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
          <span>{listError}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => {
              lastFetchKeyRef.current = null;
              loadAppraisalList();
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      <DataTable
        columns={columnsForView}
        data={displayRows}
        serverSide={false}
        disableLocalSearch={isQuarterly}
        query={tableQuery}
        onControlledQueryChange={handleTableQueryChange}
        belowSearchElement={
          <ActiveFilterChips
            chips={activeChips}
            onRemoveChip={handleRemoveChip}
            onClearAll={handleClearAllFilters}
          />
        }
        filterElement={
          <div className="d-flex align-items-center flex-wrap gap-2">
            {showStatusSummaryChips ? (
              <StatusSummaryChips
                counts={statusSummaryCounts}
                activeKey={activeStatusSummaryKey}
                onSelect={handleStatusSummarySelect}
                onClear={handleStatusSummaryClear}
              />
            ) : null}
            <Filter
              filters={resolvedFilters}
              onFiltersChange={handleFilterChange}
              showQuarterlyFilters={isQuarterly}
              loadFilterOptions={filterOptionsReady}
            />
          </div>
        }
      />

      <DeleteModal
        onDelete={deleteRecord}
        show={openDeleteModal}
        handleModalOpen={setOpenDeleteModal}
        handleClose={hideDeleteModal}
      />

      <ApprovalModal
        show={approvalModal.show}
        onHide={() => setApprovalModal({ show: false, appraisalId: null, actionType: null })}
        appraisalId={approvalModal.appraisalId}
        actionType={approvalModal.actionType}
        onSuccess={handleApprovalSuccess}
        objectViewedId={object_viewed_id}
      />
    </div>
  );
};

export default AppraisalProgressList;
